import {
  OrganizationType,
  PositionType,
  type SportYearPlayedProfileEdit,
  v1PeopleProfileEditsCreate,
  v1PositionsList,
} from '@on3/api';
import { OrganizationAutoComplete } from '@on3/ui-lib/components/AutoComplete/OrganizationAutoComplete';
import { Form } from '@on3/ui-lib/components/Form';
import { Input } from '@on3/ui-lib/components/Input/Input';
import {
  Select,
  type SelectOption,
} from '@on3/ui-lib/components/Select/Select';
import { externalApi, useToast } from '@on3/ui-lib/index';
import { filterNumber } from '@on3/ui-lib/utils/filters';
import { toNumberOrNull } from '@on3/ui-lib/utils/yup';
import { SportIcon } from '@public/components/Icons/SportIcon';
import { usePlayerProfile } from '@public/contexts/PlayerProfile/PlayerProvider';
import { useEffect, useState } from 'react';
import { number, object } from 'yup';

import styles from './UpdateSportModal.module.scss';

interface IUpdateSportModalProps {
  sportYear: SportYearPlayedProfileEdit;
  onComplete: (sy: SportYearPlayedProfileEdit) => void;
}

const schema = object({
  year: number().required().label('Year'),
  sportKey: number().required().label('Sport'),
  organizationKey: number().required().label('School'),
  primaryPositionKey: number().required().label('Position'),
  secondaryPositionKey: number().nullable().label('Secondary position'),
  jerseyNumber: number()
    .transform(toNumberOrNull)
    .nullable()
    .label('Jersey number'),
});

export const UpdateSportModal = ({
  sportYear,
  onComplete = (_sy: SportYearPlayedProfileEdit) => {},
}: IUpdateSportModalProps) => {
  const {
    state: { player },
  } = usePlayerProfile();
  const { notify } = useToast();

  const [values, setValues] = useState<SportYearPlayedProfileEdit>(sportYear);

  const [positionOptions, setPositionOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    if (!values.sportKey) return;

    try {
      v1PositionsList(externalApi, {
        positionType: PositionType.Player,
        sportKey: values.sportKey,
      }).then((res) =>
        setPositionOptions(
          res.map((p) => ({ label: p.name || '', value: String(p.key) })),
        ),
      );
    } catch (error) {
      notify('Unable to fetch positions', { type: 'error' });
    }
  }, [notify, values.sportKey]);

  const handleUpdate = (
    field: keyof SportYearPlayedProfileEdit,
    value: string | number,
  ) =>
    setValues((prev) => ({
      ...prev,
      [field]: value,
    }));

  const handleSubmit = async (data: SportYearPlayedProfileEdit) => {
    try {
      const { $type, editType, ...rest } = data;

      const { reviewItem } = await v1PeopleProfileEditsCreate(
        externalApi,
        player.key,
        {
          edit: {
            $type,
            editType,
            ...rest,
          },
        },
      );

      notify('Sport updated successfully.', { type: 'success' });

      onComplete(reviewItem.newValue as SportYearPlayedProfileEdit);
    } catch (error) {
      notify('Failed to update sport.', { type: 'error' });
    }
  };

  const isValid =
    !!values.year && !!values.sportKey && !!values.organizationKey;

  return (
    <Form
      className={styles.block}
      onSubmit={handleSubmit}
      schema={schema}
      values={values}
    >
      {({ processing, errors }) => (
        <div className={styles.sport}>
          <header className={styles.header}>
            <h2>Update Sport</h2>
            <div className={styles.sportMeta}>
              <SportIcon className={styles.icon} sportName={values.sportName} />
              <div className={styles.sportYear}>
                <span className={styles.year}>{values.year}</span>
                <h3>{values.sportName}</h3>
              </div>
            </div>
          </header>
          <div className={styles.meta}>
            <OrganizationAutoComplete
              error={errors.organizationKey}
              id="organizationKey"
              label="School"
              limit={1}
              name="school"
              onChange={(o) => {
                handleUpdate('organizationKey', o[0]?.key);
                handleUpdate('organizationName', o[0]?.label);
              }}
              placeholder="Select a school"
              required
              type={OrganizationType.HighSchool}
              value={[
                {
                  key: sportYear.organizationKey,
                  label: sportYear.organizationName ?? '',
                },
              ].filter((o) => !!o.key)}
            />
            <Input
              error={errors.jerseyNumber}
              label="Jersey Number"
              onChange={(e) =>
                handleUpdate('jerseyNumber', filterNumber(e.target.value))
              }
              type="numeric"
              value={String(values.jerseyNumber ?? '')}
            />
          </div>
          <div className={styles.positions}>
            <Select
              disabled={!values.sportKey}
              error={errors.primaryPositionKey}
              label="Position"
              onChange={(e) => {
                handleUpdate('primaryPositionKey', Number(e.target.value));
                handleUpdate(
                  'primaryPositionName',
                  positionOptions.find((p) => p.value === e.target.value)
                    ?.label ?? '',
                );
              }}
              options={positionOptions.filter(
                (p) => p.value !== String(values.secondaryPositionKey),
              )}
              placeholder="Select a position"
              required
              value={String(values.primaryPositionKey ?? '')}
            />
            <Select
              disabled={!values.sportKey}
              error={errors.secondaryPositionKey}
              label="Secondary Position"
              onChange={(e) => {
                handleUpdate('secondaryPositionKey', Number(e.target.value));
                handleUpdate(
                  'secondaryPositionName',
                  positionOptions.find((p) => p.value === e.target.value)
                    ?.label ?? '',
                );
              }}
              options={positionOptions.filter(
                (p) => p.value !== String(values.primaryPositionKey),
              )}
              placeholder="Select a position"
              value={String(values.secondaryPositionKey ?? '')}
            />
          </div>
          <button disabled={processing || !isValid} type="submit">
            Save
          </button>
        </div>
      )}
    </Form>
  );
};
